import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ExternalLink, FileText, MessageSquare, Heart, Loader2 } from 'lucide-react';
import type { InternalContentType } from '@/types/donation';
import type { LibraryFile } from '@/types/library';
import type { Prayer } from '@/types/prayers';
import type { Message } from '@/types';
import { getFile } from '@/services/library';
import { getApprovedPrayer } from '@/services/prayers';
import { getContentRoute } from '@/utils/contentNavigation';

// ============================================
// TYPES
// ============================================

interface ContentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentType: InternalContentType;
  contentId: string;
}

// ============================================
// CONTENT PREVIEW COMPONENTS
// ============================================

interface MaterialPreviewProps {
  material: LibraryFile;
}

function MaterialPreview({ material }: MaterialPreviewProps) {
  return (
    <div className="space-y-4">
      {material.thumbnailUrl && (
        <img
          src={material.thumbnailUrl}
          alt={material.title}
          className="w-full h-48 object-cover rounded-lg"
        />
      )}
      <div>
        <h3 className="text-lg font-semibold text-slate-800">{material.title}</h3>
        {material.description && <p className="text-slate-600 mt-2">{material.description}</p>}
        <div className="flex items-center gap-2 mt-3 text-sm text-slate-500">
          <span className="capitalize">{material.type}</span>
          {material.authorName && (
            <>
              <span>•</span>
              <span>by {material.authorName}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface PrayerPreviewProps {
  prayer: Prayer;
}

function PrayerPreview({ prayer }: PrayerPreviewProps) {
  const getTypeLabel = () => {
    switch (prayer.type) {
      case 'healing':
        return 'Healing Prayer';
      case 'memory':
        return 'In Memoriam';
      case 'dedication':
        return 'Study Dedication';
    }
  };

  const getTypeColor = () => {
    switch (prayer.type) {
      case 'healing':
        return 'bg-emerald-100 text-emerald-700';
      case 'memory':
        return 'bg-blue-100 text-blue-700';
      case 'dedication':
        return 'bg-purple-100 text-purple-700';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Heart size={24} className="text-[#8e4585]" />
        <span className={`text-sm px-2 py-0.5 rounded-full ${getTypeColor()}`}>
          {getTypeLabel()}
        </span>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-slate-800">{prayer.name}</h3>
        {prayer.hebrewName && (
          <p className="text-lg text-slate-600 mt-1" dir="rtl">
            {prayer.hebrewName}
          </p>
        )}
        {prayer.note && <p className="text-slate-600 mt-3">{prayer.note}</p>}
        {prayer.addedByName && (
          <p className="text-sm text-slate-500 mt-3">Added by {prayer.addedByName}</p>
        )}
      </div>
    </div>
  );
}

interface PostPreviewProps {
  message: Message;
}

function PostPreview({ message }: PostPreviewProps) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <MessageSquare size={24} className="text-[#8e4585]" />
        <div>
          <p className="font-medium text-slate-800">{message.author}</p>
          {message.timestamp && (
            <p className="text-xs text-slate-500">
              {message.timestamp.toDate?.()?.toLocaleDateString() || ''}
            </p>
          )}
        </div>
      </div>
      <p className="text-slate-700 whitespace-pre-wrap">{message.text}</p>
      {message.attachments && message.attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {message.attachments.map((att) => (
            <img key={att.url} src={att.url} alt="" className="w-24 h-24 object-cover rounded-lg" />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function ContentPreviewModal({
  isOpen,
  onClose,
  contentType,
  contentId,
}: ContentPreviewModalProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [material, setMaterial] = useState<LibraryFile | null>(null);
  const [prayer, setPrayer] = useState<Prayer | null>(null);
  const [post, setPost] = useState<Message | null>(null);

  useEffect(() => {
    if (!isOpen || !contentId) return;

    setLoading(true);
    setError(null);

    const fetchContent = async () => {
      try {
        if (contentType === 'material') {
          const file = await getFile(contentId);
          if (file) {
            setMaterial(file);
          } else {
            setError('Material not found');
          }
        } else if (contentType === 'prayer') {
          const p = await getApprovedPrayer(contentId);
          if (p) {
            setPrayer(p);
          } else {
            setError('Prayer not found');
          }
        } else if (contentType === 'post') {
          // Posts are usually accessed via subscription, we'll show a simplified view
          // For now, just show a placeholder since posts aren't easily fetched individually
          setPost({
            id: contentId,
            text: 'Community post',
            author: '',
            userId: '',
            timestamp: null,
          } as Message);
        }
      } catch (err) {
        console.error('Error fetching content:', err);
        setError('Failed to load content');
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [isOpen, contentType, contentId]);

  if (!isOpen) return null;

  const handleNavigate = () => {
    const route = getContentRoute(contentType, contentId);
    navigate(route);
    onClose();
  };

  const getTitle = () => {
    switch (contentType) {
      case 'material':
        return 'Library Material';
      case 'prayer':
        return 'Prayer';
      case 'post':
        return 'Community Post';
    }
  };

  const getIcon = () => {
    switch (contentType) {
      case 'material':
        return <FileText size={20} />;
      case 'prayer':
        return <Heart size={20} />;
      case 'post':
        return <MessageSquare size={20} />;
    }
  };

  const contentOrError = error ? (
    <div className="text-center py-12 text-slate-500">{error}</div>
  ) : (
    <>
      {contentType === 'material' && material && <MaterialPreview material={material} />}
      {contentType === 'prayer' && prayer && <PrayerPreview prayer={prayer} />}
      {contentType === 'post' && post && <PostPreview message={post} />}
    </>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#8e4585] to-[#63345e] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            {getIcon()}
            <h2 className="text-lg font-semibold">{getTitle()}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-slate-400" size={32} />
            </div>
          ) : (
            contentOrError
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-4 flex justify-between">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleNavigate}
            className="flex items-center gap-2 px-4 py-2 bg-[#8e4585] text-white rounded-lg hover:bg-[#753a6f] transition-colors"
          >
            <ExternalLink size={16} />
            View Full Content
          </button>
        </div>
      </div>
    </div>
  );
}
